<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from "#imports";

const props = withDefaults(
  defineProps<{
    fadeRight?: boolean;
    showControls?: boolean;
    controlsPosition?: string;
    showInlineControl?: boolean;
    overflow?: string;
  }>(),
  {
    fadeRight: false,
    showControls: false,
    controlsPosition: "bottom",
    showInlineControl: false,
    overflow: "hidden",
  },
);

const content = ref<HTMLElement | null>(null);
const showBackwardsControl = ref(false);
const showForwardsControl = ref(false);
const scrollAmount = ref(0);

const updateControlVisibility = () => {
  if (!content.value) {
    return;
  }

  const scrollLeft = content.value.scrollLeft;
  const maxScrollLeft = content.value.scrollWidth - content.value.offsetWidth;

  showBackwardsControl.value = scrollLeft > 0;
  showForwardsControl.value = scrollLeft < maxScrollLeft;
};

const scrollContent = (direction: "left" | "right") => {
  if (!content.value) {
    return;
  }

  scrollAmount.value = content.value.offsetWidth / 2;
  const newScrollLeft =
    content.value.scrollLeft +
    (direction === "left" ? -scrollAmount.value : scrollAmount.value);

  content.value.scrollLeft = newScrollLeft;
};

defineExpose({
  content,
});

const handleScroll = () => {
  updateControlVisibility();
};

let resizeObserver: ResizeObserver | null = null;

onMounted(async () => {
  if (content.value) {
    content.value.addEventListener("scroll", handleScroll);

    await nextTick();
    updateControlVisibility();

    if (import.meta.client && content.value) {
      resizeObserver = new ResizeObserver(() => {
        updateControlVisibility();
      });
      resizeObserver.observe(content.value);
    }
  }
});

onUnmounted(() => {
  if (content.value) {
    content.value.removeEventListener("scroll", handleScroll);
  }
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});
</script>

<template>
  <div
    :class="[
      'swipe-container',
      props.fadeRight ? 'fade-right' : '',
      props.showControls ? 'show-controls' : '',
    ]"
  >
    <div
      v-if="props.showControls"
      :class="['slider-controls', props.controlsPosition]"
    >
      <button class="arrow-button arrow-left" @click="scrollContent('left')">
        <i class="ri-arrow-left-s-line" />
      </button>
      <button class="arrow-button arrow-right" @click="scrollContent('right')">
        <i class="ri-arrow-right-s-line" />
      </button>
    </div>
    <div class="swipe-content" ref="content">
      <slot />
    </div>
    <div v-if="props.showInlineControl" class="inline-controls">
      <button
        v-if="showBackwardsControl"
        class="arrow-button arrow-left"
        @click="scrollContent('left')"
      >
        <i class="ri-arrow-left-s-line" />
      </button>
      <button
        v-if="showForwardsControl"
        class="arrow-button arrow-right"
        @click="scrollContent('right')"
      >
        <i class="ri-arrow-right-s-line" />
      </button>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
@import "assets/css/breakpoints.pcss";

.swipe-container {
  position: relative;
  width: 100%;
  overflow: v-bind(props.overflow);
}

.swipe-container.show-controls {
  padding-bottom: 2.7rem;
}

.swipe-container.show-controls:has(.top) {
  padding-top: 2rem;
  padding-bottom: 0;
}

.swipe-content {
  pointer-events: auto;
  display: inline-flex;
  align-items: baseline;
  gap: inherit;
  flex-wrap: nowrap;
  width: 100%;
  overflow-y: hidden;
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;
  min-height: fit-content;
  -ms-overflow-style: none;
  scrollbar-width: none;
  /*padding: 0.385rem 0.175rem 0.25rem;*/
  scroll-behavior: smooth;
}
.swipe-content::-webkit-scrollbar {
  display: none;
}
.swipe-content > * {
  flex: 0 0 auto;
  pointer-events: auto;
  scroll-snap-align: start;
}

.fade-right:after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 8rem;
  height: 100%;
}

@media (max-width: 768px) {
  .swipe-container {
    scroll-behavior: smooth;
  }
}

.slider-controls {
  position: absolute;
  bottom: 0.2rem;
  right: 1rem;
  display: flex;
  z-index: 1;
  pointer-events: none;
  gap: 0.75rem;
  transition: margin-right 0.3s ease;

  @media (min-width: 768px) {
    right: 1.5rem;
  }
}

.slider-controls.top {
  top: -1rem;
  right: 1rem;
}

.arrow-button {
  font-size: 1.5rem;
  padding: 00;
  color: var(--color-slate-400);
  background-color: var(--color-slate-50);
  border: 0;
  box-shadow: var(--shadow-sm);
  border-radius: 0.375rem;
  width: 2.35rem;
  height: 2.35rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  pointer-events: auto;
  box-shadow: var(--shadow-base);
}

.arrow-button:hover {
  background-color: var(--color-slate-100);
  border: 2px solid var(--color-slate-200);
}

.inline-controls {
  display: none;
  @mixin screen md {
    display: flex;
    z-index: 10;
    .arrow-button {
      border-radius: 50%;
    }
    .arrow-right {
      position: absolute;
      margin-left: 2rem;
      bottom: 50%;
      transform: translateY(50%);
      right: 0;
    }
    .arrow-left {
      position: absolute;
      bottom: 50%;
      transform: translateY(50%);
      left: 0;
    }
  }
}
</style>
